import { createContext, useContext, useEffect, useState } from "react";
import client from "@/lib/api";

/**
 * Credit-cost catalogue fetched once at app boot. Every action button reads
 * its cost from here to render inline "· N credits" labels and to decide
 * whether to show a confirm modal.
 *
 * Falls back to a sensible hard-coded default if the /api/costs call fails,
 * so the UI never renders "· undefined credits".
 */
const DEFAULT = {
    costs: {
        audit: 5,
        brief: 5,
        digest: 3,
        rank_explainer: 2,
        bulk_diagnose_base: 3,
        bulk_diagnose_per_kw: 1,
        content_analyze: 1,
        keyword_add: 1,
        backlinks_refresh: 2,
    },
    require_approval: [
        "audit", "brief", "digest", "rank_explainer",
        "bulk_diagnose_base", "bulk_diagnose_per_kw",
        "content_analyze", "keyword_add", "backlinks_refresh",
        "keywords_refresh_serp",
    ],
    confirm_modal_threshold: 5,
};

const CostsCtx = createContext(DEFAULT);

export function CostsProvider({ children }) {
    const [data, setData] = useState(DEFAULT);
    useEffect(() => {
        client.get("/costs")
            .then((r) => setData({ ...DEFAULT, ...r.data, costs: { ...DEFAULT.costs, ...r.data.costs } }))
            .catch(() => { /* keep DEFAULT */ });
    }, []);
    return <CostsCtx.Provider value={data}>{children}</CostsCtx.Provider>;
}

export const useCosts = () => useContext(CostsCtx);

/** Look up a single action's cost by key, with a fallback. */
export function useActionCost(actionKey, fallback = 0) {
    const { costs } = useCosts();
    return costs?.[actionKey] ?? fallback;
}
